import {
  Box,
  Pagination,
  Icon,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
  Timestamp,
  Timezone,
  CircleButton,
  withStyles,
  createTableProps,
  GetColorStatus,
  BankFileConversionType,
  FileConversionService,
} from 'vopay';
import { useEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Title, TransactionStatus } from 'UI';
import { parseISO, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLocation } from 'react-router-dom';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useFileConversion from '@/hooks/useApi/useFileConversion';
import FileConversionTableFilters from './FileConversionTableFilters';
import styles from './FileUploadHistory.module.scss';
import { Button, TableFiltersContainer, Table } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import NoRecordSvg from './NoRecord.svg';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useDataFetchIndicator } from '@/hooks';

import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { ConversionStatus, PaymentRail, Service, FileConversionQueryParams, FileConversionSearchParams } from './types';
import { useDialog } from '@/providers/Dialog.provider';
import DownloadFileDialog from './DownloadFileDialog/DownloadFileDialog';
import { type DownloadFileFormValues } from './DownloadFileDialog/useDownloadFileFormFields';
import ProcessFileDialog from './ProcessFileDialog/ProcessFileDialog';
import ProcessFileSuccessDialog from './ProcessFileDialog/ProcessFileSuccessDialog';
import { type ProcessFileSuccessResult } from './ProcessFileDialog/ProcessFileDialog';
import { useFileConfigurations } from '../FileConfigurationProvider';

const classes = withStyles(styles);

export interface FileConversionTableRef {
  refreshData: () => Promise<void>;
}

const FileConversionTable = forwardRef<FileConversionTableRef>((_props, ref) => {
  const location = useLocation();

  const { userSettings } = useUserSettingsProvider();
  const { history, getHistory, downloadFile, downloadSourceFile, downloadLoading } = useFileConversion();
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History.FileConversionTable' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [, setLoading] = useDataFetchIndicator();
  const { configurations, configurationsLoading } = useFileConfigurations();
  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const lockedFeatures = useLockedFeatures();
  const queryParams: FileConversionQueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      conversionStatus: (params.get('conversionStatus') || '') as ConversionStatus,
      paymentRail: (params.get('paymentRail') || '') as PaymentRail,
      service: (params.get('service') || '') as Service,
      timeZone: params.get('timeZone') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
    };
  }, [location.search]);

  const conversionStatus = useMemo(() => queryParams.conversionStatus || '', [queryParams]);
  const paymentRail = useMemo(() => queryParams.paymentRail || '', [queryParams]);
  const service = useMemo(() => queryParams.service || '', [queryParams]);
  const timeZone = useMemo(() => (queryParams.timeZone as Timezone) || userSettings.timezone || 'UTC', [queryParams]);

  const timeDifference = useMemo(() => formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const startDate = useMemo(() => queryParams.startDate || timeDifference, [queryParams, timeDifference]);
  const endDate = useMemo(() => queryParams.endDate || currentDateTime, [queryParams, currentDateTime]);

  const filtersApplied = useMemo(() => {
    return (
      !!conversionStatus ||
      !!paymentRail ||
      !!service ||
      !!(queryParams.timeZone && queryParams.timeZone !== userSettings.timezone) ||
      startDate !== timeDifference ||
      endDate !== currentDateTime
    );
  }, [
    conversionStatus,
    paymentRail,
    service,
    queryParams.timeZone,
    userSettings.timezone,
    startDate,
    endDate,
    timeDifference,
    currentDateTime,
  ]);

  const updateQueryParams = useUpdateQueryParams<FileConversionQueryParams>();

  const [initialLoad, setInitialLoad] = useState(false);

  const handleDownloadFile = async (fileConversionID: string, res: DownloadFileFormValues) => {
    try {
      setLoading(true);
      await downloadFile({
        FileConversionID: fileConversionID,
        FileConversionConfigurationID: res.FileConversionConfigurationID,
        PaymentRail: res.PaymentRail || undefined,
        ValueDate: res.ValueDate,
      });
    } catch (err) {
      alertDialog.error(
        t('downloadErrorTitle', { defaultValue: 'Download Error' }),
        t('downloadError', { defaultValue: 'Download Error' }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDialog = async (fileConversionID: string, service: FileConversionService) => {
    const res = await dialog.open<DownloadFileFormValues>(({ onCancel, onConfirm }) => (
      <DownloadFileDialog
        onCancel={onCancel}
        onConfirm={onConfirm}
        configurations={configurations}
        configurationsLoading={configurationsLoading}
        service={service}
      />
    ));

    if (!res) return;
    handleDownloadFile(fileConversionID, res);
  };

  const handleProcessDialog = async (fileConversionID: string, service: FileConversionService) => {
    const result = await dialog.open<ProcessFileSuccessResult>(({ onCancel, onConfirm }) => (
      <ProcessFileDialog
        onCancel={onCancel}
        onConfirm={onConfirm}
        fileConversionID={fileConversionID}
        configurations={configurations}
        configurationsLoading={configurationsLoading}
        service={service}
      />
    ));

    if (!result) return;

    // Show success dialog with the result
    await dialog.open(({ onCancel }) => <ProcessFileSuccessDialog onClose={onCancel} data={result} />);
    // Refresh the table
    await search(offset, {
      conversionStatus,
      paymentRail,
      service,
      timeZone,
      startDate,
      endDate,
    });
  };

  const handleDownloadSource = async (fileConversionID: string) => {
    try {
      setLoading(true);
      await downloadSourceFile({ FileConversionID: fileConversionID });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('downloadError', { defaultValue: 'Failed to download file' });
      alertDialog.error(t('downloadErrorTitle', { defaultValue: 'Download Error' }), msg);
    } finally {
      setLoading(false);
    }
  };

  // pagination
  const [totalDataLength, setTotalDataLength] = useState(0);

  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const canDownloadFile = useMemo(() => {
    const options = configurations
      .map((config) => ({
        value: config.Processor,
        label: config.FinancialInstitution,
      }))
      .filter((option): option is { value: BankFileConversionType; label: string } => option !== null);

    // If only one option and it's default_processor, return false
    if (options.length === 1 && options[0].value === 'default_processor') {
      return false;
    }
    // Otherwise, return true if there is at least one option
    return options.length > 0;
  }, [configurations]);

  // Expose refresh method to parent
  useImperativeHandle(
    ref,
    () => ({
      refreshData: async () => {
        const offset = setPageIndex(0);
        await search(offset, {
          conversionStatus,
          paymentRail,
          service,
          timeZone,
          startDate,
          endDate,
        });
      },
    }),
    [conversionStatus, paymentRail, service, timeZone, startDate, endDate, setPageIndex],
  );

  const search = async (offset = 0, searchParams: Partial<FileConversionSearchParams>) => {
    const defaultValues = {
      ...(searchParams.conversionStatus === 'all' ? {} : { ConversionStatus: searchParams.conversionStatus as any }),
      ...(searchParams.paymentRail === 'all' ? {} : { PaymentRail: searchParams.paymentRail as any }),
      ...(searchParams.service === 'all' ? {} : { Service: searchParams.service as any }),
      Timezone: searchParams.timeZone ?? timeZone,
      StartDate: searchParams.startDate ?? startDate,
      EndDate: searchParams.endDate ?? endDate,
      Limit: PER_PAGE,
      Offset: offset,
    };
    try {
      setLoading(true);
      const res = await getHistory(defaultValues);
      setTotalDataLength(res.PaginationMetaData?.TotalRecords || 0);
      if (!res.Success) throw new Error(res.ErrorMessage);

      setInitialLoad(true);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('fetchHistoryError', { defaultValue: 'Failed to fetch file conversion history' });
      alertDialog.error(t('error', { defaultValue: 'Error' }), msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams: Partial<FileConversionSearchParams> = {
      conversionStatus,
      paymentRail,
      service,
      timeZone,
      startDate: startDate || timeDifference,
      endDate: endDate || currentDateTime,
    };
    updateQueryParams({
      conversionStatus,
      paymentRail,
      service,
      timeZone,
      startDate: startDate || timeDifference,
      endDate: endDate || currentDateTime,
    });
    search(offset, searchParams);
  }, []);

  const tableProps = createTableProps({
    id: 'fileConversion-list',
    zebra: true,
    columns: [
      {
        id: 'convertedDate',
        header: t('Table.convertedDate', { defaultValue: 'Converted Date' }),
        align: 'left',
        field: 'DateAdded',
      },
      {
        id: 'originalFile',
        header: t('Table.originalFile', { defaultValue: 'Original File' }),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" gap="sm" ai="center">
            <span>{data.SourceFileName}</span>
            <CircleButton
              aria-label={t('Table.downloadOriginal')}
              icon="download"
              colorScheme="action"
              size="small"
              onClick={() => handleDownloadSource(data.FileConversionID)}
              disabled={downloadLoading}
            />
          </Box>
        ),
      },
      {
        id: 'transferType',
        header: t('Table.transferType', { defaultValue: 'Transfer Type' }),
        align: 'left',
        template: ({ data }) => {
          if (data.Service === 'Credit') {
            return tGlobal('ServiceOptions.credit');
          } else if (data.Service === 'Debit') {
            return tGlobal('ServiceOptions.debit');
          } else {
            return '-';
          }
        },
      },
      {
        id: 'paymentRail',
        header: t('Table.paymentRail', { defaultValue: 'Payment Rail' }),
        align: 'left',
        template: ({ data }) => {
          const rail = data.PaymentRail;

          if (rail) return tGlobal(`PaymentRail.${rail}`);

          return '-';
        },
      },
      {
        id: 'numberOfRecords',
        header: t('Table.numberOfRecords', { defaultValue: 'No of Records' }),
        align: 'left',
        template: ({ data }) => data.TransactionCount.toString(),
      },
      {
        id: 'totalAmount',
        header: t('Table.totalAmount', { defaultValue: 'Total Amount' }),
        align: 'left',
        template: ({ data }) => `${data.Currency} ${formattedCurrency(data.TotalAmount)}`,
      },
      {
        id: 'status',
        header: t('Table.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus status={GetColorStatus(data.ConversionStatus)} label={data.ConversionStatus} />
          </Box>
        ),
      },
      {
        id: 'action-submit',
        header: t('Table.actions', { defaultValue: 'Actions' }),
        align: 'left',
        visible: true,
        template: ({ data }) => {
          const isLocked = !lockedFeatures.FileSubmissionEnabled;
          return (
            <Box flx="row" gapSize="6px" ai="center">
              <Box flx="col" gapSize="6px" ai="center">
                <Button
                  id={`process-transactions-${data.FileConversionID}`}
                  size="small"
                  colorScheme="secondary"
                  iconRight={isLocked ? 'lock' : undefined}
                  onClick={() => {
                    if (isLocked) {
                      updateIsLocked(true, t('Table.fileSubmission', { defaultValue: 'File Submission' }));
                    } else {
                      handleProcessDialog(data.FileConversionID, data.Service);
                    }
                  }}
                  disabled={data.IsSubmitted}
                >
                  {t('Table.processTransactions')}
                </Button>
                {data.IsSubmitted && data.SubmittedDate && (
                  <div className={classes('submittedOn')}>
                    {t('Table.submittedOn', {
                      date: format(parseISO(data.SubmittedDate), 'yyyy-MM-dd'),
                      defaultValue: `Submitted On ${data.SubmittedDate}`,
                    })}
                  </div>
                )}
              </Box>
              {lockedFeatures.FileConversionEnabled && canDownloadFile && (
                <Box flx="row" gap="sm" ai="center">
                  <CircleButton
                    aria-label={t('Table.downloadOriginal', { defaultValue: 'Download original file' })}
                    icon="download"
                    colorScheme="action"
                    size="medium"
                    onClick={() => handleDownloadDialog(data.FileConversionID, data.Service)}
                    disabled={downloadLoading}
                  />
                </Box>
              )}
            </Box>
          );
        },
      },
    ],
    rows: history.map((file, index) => ({
      id: String(file.FileConversionID),
      data: { ...file, index },
    })),
  });

  return (
    <Box flx="col" gap="sm">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="fe" ai="center" base="8" gap="md">
          <TableFiltersContainer.Trigger />
        </Box>
        <TableFiltersContainer.Content>
          <FileConversionTableFilters
            service={service}
            paymentRail={paymentRail}
            conversionStatus={conversionStatus}
            timeZone={timeZone}
            initialTimeRange={[
              parseISO(startDate || timeDifference).getTime() as Timestamp,
              parseISO(endDate || currentDateTime).getTime() as Timestamp,
            ]}
            onSearch={(searchParams) => {
              const offset = setPageIndex(0);
              const params: Partial<FileConversionSearchParams> = {
                service: searchParams.service,
                paymentRail: searchParams.paymentRail,
                conversionStatus: searchParams.conversionStatus,
                timeZone: searchParams.timeZone,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
              };
              updateQueryParams({
                service: searchParams.service,
                paymentRail: searchParams.paymentRail,
                conversionStatus: searchParams.conversionStatus,
                timeZone: searchParams.timeZone,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
              });
              search(offset, params);
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>
      <Box>
        <div className={classes('tableFileConversionContainer')}>
          <Table {...tableProps} />
        </div>
      </Box>

      {!initialLoad && (
        <Box flx="col" mt="lg" jc="center">
          <Box ai="center" jc="center">
            <Icon icon="search2" size="lg" />
          </Box>
          <Box ai="center" jc="center" pt="md">
            {t('noSearchPerformed', { defaultValue: 'No search performed yet' })}
          </Box>
        </Box>
      )}
      {initialLoad && history && history.length === 0 && (
        <Box flx="col" gap="ml" mt="xl" mb="xl" pt="lg" pb="lg" base="8" jc="center">
          <Box ai="center" jc="center">
            <img src={NoRecordSvg} alt="No records" width="73" height="90" />
          </Box>
          <Box flx="col" gap="sm" base="8" jc="center">
            <Title level="3" emphasized style={{ textAlign: 'center' }}>
              {t('noConversionsFound', { defaultValue: 'No search performed yet' })}
            </Title>
            <div className={classes('noRecordMessageDescription')}>
              {t('noConversionsDescription', { defaultValue: 'No file conversions found for the selected criteria' })}
            </div>
          </Box>
        </Box>
      )}
      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              const searchParams: Partial<FileConversionSearchParams> = {
                conversionStatus,
                paymentRail,
                service,
                timeZone,
                startDate: startDate || timeDifference,
                endDate: endDate || currentDateTime,
              };
              search(offset, searchParams);
            }}
          />
        )}
      </Box>
    </Box>
  );
});

FileConversionTable.displayName = 'FileConversionTable';

export default FileConversionTable;
